import { Box } from '@mantine/core';
import { keyframes } from '../utils/keyframes';
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

export type BounceDirection = 'up' | 'down' | 'left' | 'right';

export interface BounceProps {
  children: React.ReactNode;
  active?: boolean;
  duration?: number;
  height?: number;
  direction?: BounceDirection;
  infinite?: boolean;
  count?: number;
  delay?: number;
}

const Bounce = forwardRef<HTMLDivElement, BounceProps>(
  (
    {
      children,
      active = true,
      duration = 1000,
      height = 20,
      direction = 'up',
      infinite = true,
      count = 1,
      delay = 0,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();

    if (reducedMotion || !active) {
      return <Box ref={ref}>{children}</Box>;
    }

    const iterationCount = infinite ? 'infinite' : count;

    const getTransform = (value: number) => {
      switch (direction) {
        case 'up':
          return `translateY(-${value}px)`;
        case 'down':
          return `translateY(${value}px)`;
        case 'left':
          return `translateX(-${value}px)`;
        case 'right':
          return `translateX(${value}px)`;
      }
    };

    const bounceKeyframes = keyframes({
      '0%, 20%, 50%, 80%, 100%': { transform: 'translate(0)' },
      '40%': { transform: getTransform(height) },
      '60%': { transform: getTransform(height * 0.5) },
    });

    return (
      <Box
        ref={ref}
        style={{
          animation: `${bounceKeyframes} ${duration}ms ease ${iterationCount}`,
          animationDelay: `${delay}ms`,
        }}
      >
        {children}
      </Box>
    );
  }
);

Bounce.displayName = 'Bounce';

export default Bounce;
